import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Play, Pause, Music, MoreHorizontal, Trash2, Edit, Globe, Lock, ListMusic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const PlaylistsPage = () => {
  const { user, token } = useAuth();
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '', is_public: true });
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (token) {
      fetchPlaylists();
    } else {
      setLoading(false);
    }
    fetchRecommendations();
  }, [token]);

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylists(response.data);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/recommendations/tracks?limit=10`, { headers });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const fetchPlaylistDetails = async (playlistId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/playlists/${playlistId}`, { headers });
      setSelectedPlaylist(response.data);
    } catch (error) {
      toast.error('Failed to load playlist');
    }
  };

  const createPlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }

    try {
      await axios.post(`${API}/playlists`, newPlaylist, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Playlist created!');
      setCreateDialogOpen(false);
      setNewPlaylist({ name: '', description: '', is_public: true });
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    try {
      await axios.delete(`${API}/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Playlist deleted');
      fetchPlaylists();
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
      }
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const removeTrackFromPlaylist = async (trackId) => {
    if (!selectedPlaylist) return;
    try {
      await axios.delete(`${API}/playlists/${selectedPlaylist.id}/tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Track removed');
      fetchPlaylistDetails(selectedPlaylist.id);
    } catch (error) {
      toast.error('Failed to remove track');
    }
  };

  const addToPlaylist = async (playlistId, trackId) => {
    try {
      await axios.post(`${API}/playlists/${playlistId}/tracks/${trackId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Added to playlist!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to playlist');
    }
  };

  const playPlaylist = (playlist) => {
    if (playlist.tracks?.length > 0) {
      playTrack(playlist.tracks[0]);
    }
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto pb-24" data-testid="playlists-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <ListMusic size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-bold mb-2">Login to Create Playlists</h2>
          <p className="text-muted-foreground mb-6">Save your favorite tracks and create custom playlists</p>
          
          {/* Show recommendations for non-logged in users */}
          <div className="mt-8 text-left">
            <h3 className="text-lg font-semibold mb-4">Recommended Tracks</h3>
            <div className="grid gap-2">
              {recommendations.slice(0, 5).map((track) => (
                <TrackItem key={track.id} track={track} onPlay={() => playTrack(track)} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 pb-24">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="playlists-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold">Your Playlists</h1>
          <p className="text-muted-foreground">Create and manage your music collections</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus size={16} className="mr-2" />
              New Playlist
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Playlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="playlist-name">Name</Label>
                <Input
                  id="playlist-name"
                  value={newPlaylist.name}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                  placeholder="My Awesome Playlist"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="playlist-desc">Description</Label>
                <Textarea
                  id="playlist-desc"
                  value={newPlaylist.description}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, description: e.target.value })}
                  placeholder="What's this playlist about?"
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {newPlaylist.is_public ? <Globe size={16} /> : <Lock size={16} />}
                  <Label htmlFor="playlist-public">Public playlist</Label>
                </div>
                <Switch
                  id="playlist-public"
                  checked={newPlaylist.is_public}
                  onCheckedChange={(checked) => setNewPlaylist({ ...newPlaylist, is_public: checked })}
                />
              </div>
              <Button onClick={createPlaylist} className="w-full">Create Playlist</Button>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Playlists Grid */}
        <div className="lg:col-span-2">
          {playlists.length === 0 ? (
            <Card className="bg-card/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ListMusic size={48} className="text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No playlists yet</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Create your first playlist to start organizing your music
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus size={16} className="mr-2" />
                  Create Playlist
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {playlists.map((playlist) => (
                <PlaylistCard
                  key={playlist.id}
                  playlist={playlist}
                  onSelect={() => fetchPlaylistDetails(playlist.id)}
                  onDelete={() => deletePlaylist(playlist.id)}
                  onPlay={() => playPlaylist(playlist)}
                  isSelected={selectedPlaylist?.id === playlist.id}
                />
              ))}
            </div>
          )}

          {/* Selected Playlist Details */}
          {selectedPlaylist && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6"
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{selectedPlaylist.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {selectedPlaylist.tracks?.length || 0} tracks
                      </p>
                    </div>
                    <Button onClick={() => playPlaylist(selectedPlaylist)} disabled={!selectedPlaylist.tracks?.length}>
                      <Play size={16} className="mr-2" />
                      Play All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedPlaylist.tracks?.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">
                      No tracks in this playlist yet. Add tracks from recommendations!
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedPlaylist.tracks?.map((track, index) => (
                        <TrackItem
                          key={track.id}
                          track={track}
                          index={index + 1}
                          onPlay={() => playTrack(track)}
                          onRemove={() => removeTrackFromPlaylist(track.id)}
                          isPlaying={currentTrack?.id === track.id && isPlaying}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Recommendations Sidebar */}
        <div className="space-y-4">
          <h3 className="font-semibold">Recommended for You</h3>
          <div className="space-y-2">
            {recommendations.map((track) => (
              <div key={track.id} className="flex items-center gap-2">
                <TrackItem
                  track={track}
                  onPlay={() => playTrack(track)}
                  compact
                />
                {playlists.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="shrink-0">
                        <Plus size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {playlists.map((playlist) => (
                        <DropdownMenuItem
                          key={playlist.id}
                          onClick={() => addToPlaylist(playlist.id, track.id)}
                        >
                          Add to "{playlist.name}"
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlaylistCard = ({ playlist, onSelect, onDelete, onPlay, isSelected }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`relative group cursor-pointer rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/50 to-blue-900/50 aspect-square ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
      onClick={onSelect}
    >
      {playlist.cover_url ? (
        <img src={playlist.cover_url} alt={playlist.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Music size={48} className="text-white/30" />
        </div>
      )}
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <h4 className="font-semibold text-white truncate">{playlist.name}</h4>
        <p className="text-xs text-white/70">
          {playlist.track_ids?.length || 0} tracks
          {playlist.is_public ? '' : ' • Private'}
        </p>
      </div>

      {/* Hover Actions */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="bg-black/50 text-white">
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPlay(); }}>
              <Play size={14} className="mr-2" /> Play
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-red-500"
            >
              <Trash2 size={14} className="mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Play Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onPlay(); }}
        className="absolute bottom-12 right-3 w-10 h-10 rounded-full bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
      >
        <Play size={20} className="text-white ml-0.5" />
      </button>
    </motion.div>
  );
};

const TrackItem = ({ track, index, onPlay, onRemove, isPlaying, compact }) => {
  return (
    <div 
      className={`flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer group ${
        isPlaying ? 'bg-primary/10' : ''
      } ${compact ? 'flex-1' : ''}`}
      onClick={onPlay}
    >
      {index && (
        <span className="w-6 text-center text-sm text-muted-foreground">
          {isPlaying ? <Pause size={14} className="text-primary" /> : index}
        </span>
      )}
      
      <div className="w-10 h-10 rounded bg-muted overflow-hidden shrink-0">
        {track.thumbnail_url || track.image_url ? (
          <img 
            src={track.thumbnail_url || track.image_url} 
            alt={track.title} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music size={16} className="text-muted-foreground" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${isPlaying ? 'text-primary' : ''}`}>
          {track.title}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {track.artist_name || track.artist}
        </p>
      </div>

      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="opacity-0 group-hover:opacity-100"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Trash2 size={14} className="text-muted-foreground" />
        </Button>
      )}
    </div>
  );
};

export default PlaylistsPage;
